import {
  getUsersByIdsOptions,
  snackSupplierOptions,
  useBuilding,
  useFoodAdminOverview,
} from "@/api";
import AdminOverviewTimeFilter from "@/components/food/AdminOverviewTimeFilter";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import {
  calculateNetPrice,
  computedNumberSortingFn,
  dateSortingFn,
  dateToMonthFilterValue,
  downloadArrayAsCSV,
  downloadArrayAsXlsx,
  EXCEL_NOK_FORMAT,
  NOK,
  NUMERIC_DATE_TIME_LOCALE,
  ZONED_ISO_DATE,
} from "@/lib";
import {
  FoodAdminGuestLunchTransaction,
  FoodAdminLunchTransaction,
  FoodAdminTransaction,
  FoodAdminWalletTransaction,
  TimeframeFilterValue,
  MinimalUser,
} from "@/types";
import type { TFunction } from "i18next";
import {
  Button,
  DropdownMenu,
  Icon,
  InputField,
  PopupCard,
  SelectRow,
  StatusText,
} from "@app-components";
import { createFileRoute } from "@tanstack/react-router";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/styles/CompanySettings.module.scss";
import { AnimatedTickingNumber } from "@/components/AnimatedTickingNumber";
import { useSessionState } from "@/hooks/useSessionState";
import { Tooltip } from "react-tooltip";
import { AnimatePresence, motion } from "framer-motion";
import { createColumnHelper } from "@tanstack/react-table";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import Table from "@/components/table/Table";
import AdminTransactionModal from "@/components/food/AdminTransactionModal";
import AdminCostDistributionInfo from "@/components/food/AdminCostDistributionInfo";
import { useBuildingQuery } from "@/components/RouterSetup";
import { useUsersMappings } from "@/hooks/useUsersMappings";
import React from "react";

type CompanyOverviewRow = {
  companyName: string;
  totalSales: number;
  spentBalance: number;
  spentCredit: number;
  amountSubsidized: number;
  totalInvoiceable: number;
};

const columnHelper = createColumnHelper<FoodAdminTransaction>();
const companyColumnHelper = createColumnHelper<CompanyOverviewRow>();
const searchableColumns = ["transactionId", "type", "amount"] as const;

// Shared by the description cell and search, so both match what the admin sees.
const describeTransaction = (tx: FoodAdminTransaction, t: TFunction) => {
  if (tx.type === "topup") return t("PAGES.LUNCH.TRANSACTION_TYPES.TOPUP");
  if (tx.type === "refund") return t("PAGES.LUNCH.TRANSACTION_TYPES.REFUND");
  if (tx.type === "guest-lunch")
    return t("PAGES.LUNCH.TRANSACTION_TYPES.GUEST_LUNCH_EATEN", {
      guestCount: tx.guestCount,
    });
  return `${t("PAGES.LUNCH.TRANSACTION_TYPES.LUNCH_EATEN")} (${tx.lunchVariantName})`;
};

const simpleFlexStyles: CSSProperties = {
  display: "flex",
  gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-sm)",
  flexWrap: "wrap",
  alignItems: "center",
};

export const Route = createFileRoute("/_portal/lunch-admin/overview/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const { data: building } = useBuilding();
  const { allowFoodAdminAccess } = useWelcomeFoodAccess();

  const initialTimeframe = dateToMonthFilterValue(new Date());
  const [timeframe, setTimeframe] = useSessionState<TimeframeFilterValue>(
    "food-admin-overview-timeframe",
    initialTimeframe,
  );
  const { data: overview, isLoading } = useFoodAdminOverview(timeframe);
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const [tab, setTab] = useSessionState<string>(
    "food-admin-overview-tab",
    "transactions",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTransactions, setFilteredTransactions] = useState(
    overview?.transactions ?? [],
  );
  const [filteredCompanies, setFilteredCompanies] = useState<
    CompanyOverviewRow[]
  >([]);
  const [openTransaction, setOpenTransaction] = useState<
    FoodAdminWalletTransaction | undefined
  >();

  const uniqueUserIds = useMemo(
    () => [
      ...new Set(overview?.transactions.flatMap((t) => t.userId ?? []) ?? []),
    ],
    [overview?.transactions],
  );
  const { data: users } = useBuildingQuery(
    getUsersByIdsOptions(uniqueUserIds, !!overview),
  );
  const usersByIdMap = useMemo(
    () => new Map<string, MinimalUser>(users?.map((u) => [u.userId, u]) ?? []),
    [users],
  );
  const { usersOrgNrMap, companiesOrgNrMap, subdivisionIdToNameMap } =
    useUsersMappings(overview?.transactions, { subdivisionNames: true }, users);

  useEffect(() => {
    const transactions = overview?.transactions ?? [];
    const searchString = searchTerm.trim().toLocaleLowerCase();

    if (searchString) {
      const filteredData = transactions.filter((trans) => {
        return searchableColumns.some((column) => {
          const columnVal = (
            column === "type" ? describeTransaction(trans, t) : trans[column]
          )
            .toString()
            .toLowerCase();
          return columnVal.includes(searchString);
        });
      });
      setFilteredTransactions(filteredData);
    } else {
      setFilteredTransactions(transactions);
    }
  }, [searchTerm, overview?.transactions, t]);

  const companyRows = useMemo<CompanyOverviewRow[]>(() => {
    const agg = new Map<
      string,
      {
        totalSales: number;
        spentBalance: number;
        spentCredit: number;
        amountSubsidized: number;
      }
    >();
    for (const tx of overview?.transactions ?? []) {
      if (tx.type !== "lunch") continue;
      const existing = agg.get(tx.companyName) ?? {
        totalSales: 0,
        spentBalance: 0,
        spentCredit: 0,
        amountSubsidized: 0,
      };
      existing.totalSales += tx.dealPrice;
      existing.spentBalance += tx.paidFromBalance;
      existing.spentCredit += tx.paidFromCredit;
      existing.amountSubsidized += tx.companySubsidy;
      agg.set(tx.companyName, existing);
    }
    return Array.from(agg.entries()).map(([companyName, totals]) => ({
      companyName,
      totalSales: totals.totalSales,
      spentBalance: totals.spentBalance,
      spentCredit: totals.spentCredit,
      amountSubsidized: totals.amountSubsidized,
      totalInvoiceable: totals.spentCredit + totals.amountSubsidized,
    }));
  }, [overview?.transactions]);

  useEffect(() => {
    const searchString = searchTerm.trim().toLocaleLowerCase();
    if (searchString) {
      setFilteredCompanies(
        companyRows.filter((row) =>
          row.companyName.toLocaleLowerCase().includes(searchString),
        ),
      );
    } else {
      setFilteredCompanies(companyRows);
    }
  }, [searchTerm, companyRows]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("transactionId", {
        cell: (info) => <span style={{ opacity: 0.5 }}>{info.getValue()}</span>,
        header: t("FOOD_ADMIN.OVERVIEW.TRANSACTION_ID"),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.TRANSACTION_ID"),
        },
      }),
      columnHelper.accessor("date", {
        cell: (info) =>
          NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            new Date(info.getValue()),
          ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.TIMESTAMP")}
          />
        ),
        sortingFn: dateSortingFn,
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.TIMESTAMP"),
        },
      }),
      columnHelper.accessor("userId", {
        id: "userName",
        cell: (info) => {
          const tx = info.row.original;
          if (tx.type === "guest-lunch") return t("FOOD_ADMIN.DEALS.GUEST");
          const user = usersByIdMap.get(tx.userId);
          return user
            ? `${user.firstName} ${user.lastName}`
            : `[${t("UNKNOWN")} ${t("SNACK_ADMIN.TABLE.CUSTOMER").toLowerCase()}]`;
        },
        header: t("SNACK_ADMIN.TABLE.CUSTOMER"),
        meta: { label: t("SNACK_ADMIN.TABLE.CUSTOMER") },
      }),
      columnHelper.accessor("type", {
        cell: (info) => describeTransaction(info.row.original, t),
        header: t("FOOD_ADMIN.OVERVIEW.DESCRIPTION"),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.DESCRIPTION"),
        },
      }),
      columnHelper.accessor("type", {
        id: "amount",
        cell: (info) => {
          const type = info.getValue();
          const transaction = info.row.original;
          if (type === "topup") {
            return (
              <span
                style={{
                  color: "var(--wlcm-color-less-inactive-grey)",
                  fontWeight: "bold",
                }}
              >
                {NOK.format(Math.abs(transaction.amount))}
              </span>
            );
          }
          if (type === "refund") {
            return (
              <span
                style={{
                  color: "var(--wlcm-color-danger-red)",
                  fontWeight: "bold",
                }}
              >
                {NOK.format(Math.abs(transaction.amount))}
              </span>
            );
          }
          if (type === "guest-lunch") {
            return (
              <strong style={{ color: "var(--wlcm-color-success-green)" }}>
                {NOK.format(transaction.amount)}
              </strong>
            );
          }
          const { dealPrice, companySubsidy, paidFromCredit } =
            transaction as FoodAdminLunchTransaction;
          const lunchWasSubsidized = !!companySubsidy || !!paidFromCredit;
          return (
            <span>
              <strong style={{ color: "var(--wlcm-color-success-green)" }}>
                {NOK.format(dealPrice)}
              </strong>
              {lunchWasSubsidized && (
                <AdminCostDistributionInfo
                  {...(transaction as FoodAdminLunchTransaction)}
                  lunchWasSubsidized={lunchWasSubsidized}
                />
              )}
            </span>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.AMOUNT")}
          />
        ),
        sortingFn: computedNumberSortingFn((row) =>
          row.type === "lunch" ? row.dealPrice : Math.abs(row.amount),
        ),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.AMOUNT"),
          style: { textAlign: "right", display: "flex", justifyContent: "end" },
        },
      }),
      columnHelper.accessor("userId", {
        id: "actions",
        cell: (info) =>
          info.row.original.type === "guest-lunch" ? null : (
            <>
              <div className="rowActions">
                <Icon name="receipt" className={"transaction-receipt-icon"} />
              </div>
              <Tooltip
                anchorSelect=".transaction-receipt-icon"
                content={t("FOOD_ADMIN.OVERVIEW.SEE_DETAILS")}
              />
            </>
          ),
        header: () => (
          <div className="sr-only">{t("BUILDING_VISITORS.ACTIONS")}</div>
        ),
      }),
    ],
    [t, i18n.language, usersByIdMap],
  );

  const companiesColumns = useMemo(
    () => [
      companyColumnHelper.accessor("companyName", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.COMPANIES.COMPANY_NAME")}
          />
        ),
        meta: { label: t("FOOD_ADMIN.OVERVIEW.COMPANIES.COMPANY_NAME") },
      }),
      companyColumnHelper.accessor("spentBalance", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.COMPANIES.SPENT_BALANCE")}
            style={{ justifyContent: "end" }}
          />
        ),
        cell: (info) => NOK.format(info.getValue()),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.COMPANIES.SPENT_BALANCE"),
          style: { textAlign: "right" as const },
        },
      }),
      companyColumnHelper.accessor("spentCredit", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.COMPANIES.SPENT_CREDIT")}
            style={{ justifyContent: "end" }}
          />
        ),
        cell: (info) => (
          <strong style={{ color: "var(--wlcm-color-blue)" }}>
            {NOK.format(info.getValue())}
          </strong>
        ),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.COMPANIES.SPENT_CREDIT"),
          style: { textAlign: "right" as const },
        },
      }),
      companyColumnHelper.accessor("amountSubsidized", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.COMPANIES.AMOUNT_SUBSIDIZED")}
            style={{ justifyContent: "end" }}
          />
        ),
        cell: (info) => (
          <strong style={{ color: "var(--wlcm-color-malling-purple)" }}>
            {NOK.format(info.getValue())}
          </strong>
        ),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.COMPANIES.AMOUNT_SUBSIDIZED"),
          style: { textAlign: "right" as const },
        },
      }),
      companyColumnHelper.accessor("totalSales", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.COMPANIES.TOTAL_SALES")}
            style={{ justifyContent: "end" }}
          />
        ),
        cell: (info) => (
          <strong style={{ color: "var(--wlcm-color-success-green)" }}>
            {NOK.format(info.getValue())}
          </strong>
        ),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.COMPANIES.TOTAL_SALES"),
          style: { textAlign: "right" as const },
        },
      }),
      companyColumnHelper.accessor("totalInvoiceable", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.OVERVIEW.COMPANIES.TOTAL_INVOICEABLE")}
            columnInfo={t(
              "FOOD_ADMIN.OVERVIEW.COMPANIES.TOTAL_INVOICEABLE_INFO",
            )}
            style={{ justifyContent: "end" }}
          />
        ),
        cell: (info) => NOK.format(info.getValue()),
        meta: {
          label: t("FOOD_ADMIN.OVERVIEW.COMPANIES.TOTAL_INVOICEABLE"),
          style: { textAlign: "right" as const },
        },
      }),
    ],
    [t],
  );

  const handleExport = (type?: "excel" | "csv") => {
    if (!overview) return;
    const exportFileName = `${timeframe}_lunch-transactions`;
    const headers = [
      "building",
      "restaurantName",
      "companyName",
      "subdivision",
      "orgNumber",
      "userId",
      "userName",
      "userEmail",
      "date",
      "dateTime",
      "type",
      "topupAmount",
      "refundAmount",
      "lunchVariantName",
      "quantity",
      "price",
      "netPrice",
      "vat",
      "companyPay",
      "netCompanyPay",
      "companyPayVat",
      "employeePay",
      "netEmployeePay",
      "employeePayVat",
      "guestPay",
    ];

    const exportData = overview.transactions.map(
      ({ companyName, userId, date, type, amount, ...rest }) => {
        const {
          lunchVariantName,
          dealPrice,
          companySubsidy,
          paidFromCredit,
          paidFromBalance,
        } =
          type === "lunch" ? (rest as Partial<FoodAdminLunchTransaction>) : {};
        const guest =
          type === "guest-lunch"
            ? (rest as Pick<
                FoodAdminGuestLunchTransaction,
                "guestCount" | "guestLunchGrossPrice" | "vatAmount"
              >)
            : undefined;

        const companyPay =
          companySubsidy !== undefined && paidFromCredit !== undefined
            ? companySubsidy + paidFromCredit
            : undefined;
        const { netPrice, vatAmount: totalVat } =
          typeof dealPrice === "number" ? calculateNetPrice(dealPrice, 25) : {};
        const { netPrice: netCompanyPay, vatAmount: companyPayVat } =
          companyPay !== undefined ? calculateNetPrice(companyPay, 25) : {};
        const { netPrice: netEmployeePay, vatAmount: employeePayVat } =
          paidFromBalance !== undefined
            ? calculateNetPrice(paidFromBalance, 25)
            : {};
        const txUser = userId ? usersByIdMap.get(userId) : undefined;

        return {
          building: building.displayName,
          restaurantName: supplier?.name,
          companyName,
          subdivision: txUser?.subdivisionId
            ? (subdivisionIdToNameMap.get(txUser.subdivisionId) ?? "")
            : "",
          orgNumber:
            (userId && usersOrgNrMap.get(userId)) ||
            (txUser && companiesOrgNrMap.get(txUser.buildingTenantId)) ||
            "",
          userId,
          userName: txUser
            ? `${txUser.firstName} ${txUser.lastName}`
            : undefined,
          userEmail: txUser?.email,
          date,
          dateTime: date,
          type,
          topupAmount: type === "topup" ? amount : undefined,
          refundAmount: type === "refund" ? Math.abs(amount) : undefined,
          lunchVariantName,
          quantity: type === "lunch" ? 1 : guest?.guestCount,
          price: guest ? guest.guestLunchGrossPrice : dealPrice,
          // Guest net and VAT cover the whole payment (all covers), never divided per cover.
          netPrice: guest
            ? Math.round((amount - guest.vatAmount) * 100) / 100
            : netPrice,
          vat: guest ? guest.vatAmount : totalVat,
          companyPay,
          netCompanyPay,
          companyPayVat,
          employeePay: paidFromBalance,
          netEmployeePay,
          employeePayVat,
          guestPay: guest ? amount : undefined,
        };
      },
    );

    const customValueHandlers = {
      date: (date: FoodAdminTransaction["date"]) =>
        ZONED_ISO_DATE.format(new Date(date)),
    };

    if (type === "excel") {
      downloadArrayAsXlsx(
        exportData,
        exportFileName,
        headers,
        customValueHandlers,
        {
          topupAmount: EXCEL_NOK_FORMAT,
          refundAmount: EXCEL_NOK_FORMAT,
          price: EXCEL_NOK_FORMAT,
          netPrice: EXCEL_NOK_FORMAT,
          vat: EXCEL_NOK_FORMAT,
          companyPay: EXCEL_NOK_FORMAT,
          netCompanyPay: EXCEL_NOK_FORMAT,
          companyPayVat: EXCEL_NOK_FORMAT,
          employeePay: EXCEL_NOK_FORMAT,
          netEmployeePay: EXCEL_NOK_FORMAT,
          employeePayVat: EXCEL_NOK_FORMAT,
          guestPay: EXCEL_NOK_FORMAT,
        },
      );
    } else {
      downloadArrayAsCSV(
        exportData,
        exportFileName,
        headers,
        customValueHandlers,
      );
    }
  };

  if (!allowFoodAdminAccess) return null;
  return (
    <>
      <div className="pageHeader">
        <h1>{t("SIDEBAR.LUNCH_OVERVIEW")}</h1>
        <p>{t("FOOD_ADMIN.OVERVIEW.SUBHEADER")}</p>
      </div>
      <div
        style={{
          ...simpleFlexStyles,
          flexWrap: "wrap-reverse",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            ...simpleFlexStyles,
          }}
        >
          <AdminOverviewTimeFilter
            value={timeframe}
            onChange={setTimeframe}
            isLoading={isLoading}
          />
          <AnimatePresence initial={false} mode="popLayout">
            {timeframe !== initialTimeframe && (
              <motion.div
                layout
                initial={{ x: -10, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -10, opacity: 0 }}
              >
                <Button
                  id="food-admin--reset-timeframe"
                  dark
                  circular
                  icon="undo"
                  style={{
                    marginRight: "var(--wlcm-spacing-sm)",
                    width: 40,
                    height: 40,
                  }}
                  onClick={() => setTimeframe(initialTimeframe)}
                >
                  <span className="sr-only">
                    {t("FOOD_ADMIN.OVERVIEW.BACK_TO_CURRENT_MONTH")}
                  </span>
                </Button>
                <Tooltip
                  anchorSelect="#food-admin--reset-timeframe"
                  content={t("FOOD_ADMIN.OVERVIEW.BACK_TO_CURRENT_MONTH")}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <DropdownMenu
          id="lunch-transactions-export"
          activator={
            <Button secondary icon="file_export">
              {t("FOOD_ADMIN.OVERVIEW.EXPORT_TO_ACCOUNTING")}
            </Button>
          }
          menuOptions={[
            {
              icon: "csv",
              label: "CSV",
              onSelect: handleExport,
            },
            {
              icon: "microsoft_excel",
              label: "Microsoft Excel",
              onSelect: () => handleExport("excel"),
            },
          ]}
        />
      </div>
      <div
        style={{
          margin: "var(--wlcm-spacing-sm) 0",
          display: "flex",
          gap: "var(--wlcm-spacing-sm)",
          flexWrap: "wrap",
        }}
      >
        <h3 className={styles.numberCard} style={{ minWidth: 250 }}>
          {t("FOOD_ADMIN.OVERVIEW.SALES_IN_PERIOD")}
          <span className="sr-only">: </span>
          <strong>
            <AnimatedTickingNumber
              to={isLoading ? 0 : overview?.summary.totalSales}
              formatter={NOK.format}
            />
          </strong>
        </h3>
        <h3
          className={`${styles.numberCard} ${styles.blue}`}
          style={{ minWidth: 250 }}
        >
          {t("FOOD_ADMIN.OVERVIEW.COMPANIES.SPENT_CREDIT")}
          <span className="sr-only">: </span>
          <strong>
            <AnimatedTickingNumber
              to={isLoading ? 0 : overview?.summary.totalPaidFromCredit}
              formatter={NOK.format}
            />
          </strong>
        </h3>
        <h3
          className={`${styles.numberCard} ${styles.purple}`}
          style={{ minWidth: 250 }}
        >
          {t("FOOD_ADMIN.OVERVIEW.COMPANIES.AMOUNT_SUBSIDIZED")}
          <span className="sr-only">: </span>
          <strong>
            <AnimatedTickingNumber
              to={isLoading ? 0 : overview?.summary.totalCompanySubsidy}
              formatter={NOK.format}
            />
          </strong>
        </h3>
        <h3
          className={`${styles.numberCard} ${styles.gray}`}
          style={{ minWidth: 250 }}
        >
          {t("FOOD_ADMIN.OVERVIEW.TOTAL_UNUSED_BALANCE")}
          <span className="sr-only">: </span>
          <strong>
            <AnimatedTickingNumber
              to={isLoading ? 0 : overview?.summary.totalUnusedBalance}
              formatter={NOK.format}
            />
          </strong>
        </h3>
      </div>
      <div style={simpleFlexStyles}>
        <SelectRow
          tabs
          hideLabel
          name="food-admin-overview-tabs"
          label="Tabs"
          options={[
            {
              value: "transactions",
              label: t("PAGES.LUNCH.TRANSACTIONS"),
            },
            {
              value: "companies",
              label: t("PAGES.COMPANIES.HEADER"),
            },
          ]}
          value={tab}
          onChange={(t) => {
            setSearchTerm("");
            setTab(t as string);
          }}
        />
        <InputField
          compact
          icon="search"
          label={t(
            `FOOD_ADMIN.OVERVIEW.SEARCH_IN_${tab === "transactions" ? "TRANSACTIONS" : "COMPANIES"}`,
          )}
          hideLabel
          placeholder={
            t(
              `FOOD_ADMIN.OVERVIEW.SEARCH_IN_${tab === "transactions" ? "TRANSACTIONS" : "COMPANIES"}`,
            ) + "..."
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      {tab === "transactions" ? (
        <Table
          key="transactions"
          name="food-admin-overview-table"
          columns={columns}
          data={filteredTransactions}
          isLoading={isLoading}
          defaultSorting={[{ id: "date", desc: true }]}
          noDataMessage={t("FOOD_ADMIN.OVERVIEW.NO_TRANSACTIONS")}
          onRowClick={(tx) => {
            if (tx.type !== "guest-lunch") setOpenTransaction(tx);
          }}
          getRowClassName={(tx) =>
            tx.type === "guest-lunch" ? "notClickable" : ""
          }
          withPagination
        />
      ) : (
        <React.Fragment key="companies">
          <Table
            name="food-admin-companies-overview-table"
            columns={companiesColumns}
            data={filteredCompanies}
            isLoading={isLoading}
            defaultSorting={[{ id: "companyName", desc: false }]}
            noDataMessage={t("FOOD_ADMIN.OVERVIEW.NO_COMPANIES")}
            withPagination
          />
          <StatusText
            text={t("FOOD_ADMIN.OVERVIEW.COMPANIES_TABLE_INFO")}
            style={{ marginTop: "var(--wlcm-spacing-xs)" }}
          />
        </React.Fragment>
      )}
      <PopupCard
        open={!!openTransaction}
        onClose={() => setOpenTransaction(undefined)}
      >
        <AdminTransactionModal
          transaction={openTransaction!}
          user={usersByIdMap.get(openTransaction?.userId ?? "")}
        />
      </PopupCard>
    </>
  );
}
